import { ChangeDetectionStrategy, Component, computed, inject, OnInit, signal } from '@angular/core';
import { tableDataService } from '../../../../core/services/table-data/table-data';
import { WizardFormSubmitService } from '../../../../core/services/wizard-form-submit/wizard-form-submit.service';
import { Router, NavigationEnd, RouterOutlet } from '@angular/router';
import { filter } from 'rxjs';
import {
  FilterState,
  SortDir,
} from '../../../../shared/models/pagination';
import { CollezioniResponse } from '../../../../shared/models/collezione';
import { mainSrc, wizardResponse } from '../../../../shared/models/wizardData';
import {
  TableComponent,
  ConfigTableModel,
  PageLayoutComponent,
  SearchComponent,
  ModalService,
  EmptyBoxComponent,
  EmptyStateConfig,
  PaginatorComponent,
  ConfirmDialogComponent,
  SnackbarService,
} from '@mic-ac/ui-kit';
import { NuovaCollezioneModalComponent } from './nuova-collezione-modal.component';

@Component({
    selector: 'app-user-init-page',
    imports: [
        TableComponent,
        PageLayoutComponent,
        SearchComponent,
        EmptyBoxComponent,
        PaginatorComponent,
        RouterOutlet,

    ],
    templateUrl: './user-init-page.component.html',
    styleUrl: './user-init-page.component.css',
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MyAreaPageComponent implements OnInit {
  #tableService = inject(tableDataService);
  #wizardService = inject(WizardFormSubmitService);
  #router = inject(Router);
  #modalService = inject(ModalService);
  #snackbar = inject(SnackbarService);

  /** Impostare a true per aprire la modale del titolo prima di accedere al wizard */
  readonly useModal = true;

  row = signal<any[]>([]);
  childActive = signal(false);
  loading = signal(false);
  searchTerm = signal<string | null>(null);

  hasSearchTerm = computed(() => !!this.searchTerm()?.trim());

  // stato iniziale
  state = signal<FilterState>({
    paginazione: {
      totalPages: 0,
      totalElements: 0,
      totalElementsForPage: 5,
      page: 0,
    },
    order: 'asc',
    orderField: 'titolo',
  });

  currentPage = computed(() => this.state().paginazione.page + 1);
  totalPages = computed(() => this.state().paginazione.totalPages);

  // action button per page-layout
  actionButton = { label: 'Aggiungi', classe: 'primary custom-button-sm' };



  // configurazione empty state
 emptyStateConfig: EmptyStateConfig = {
    title: 'Nessuna collezione presente',
    subtitleAction: {
      textBefore: 'Premi il tasto',
      actionText: 'Aggiungi',
      textAfter: 'per inserire le tue collezioni digitali.'
    }
  };

  emptySearchStateConfig: EmptyStateConfig = {
    title: 'Nessuna collezione trovata',
    subtitle: 'Prova a modificare il testo di ricerca e i filtri applicati.'
  };

  // configurazione tabella dpac-table
  tableConfig = signal<ConfigTableModel>({
    labelEmpty: 'Nessun dato disponibile',
    headerConfigColumn: [
      { column: 'copertina', header: 'Copertina', template: 'image', width: '80px' },
      { column: 'titolo', header: 'Titolo', template: 'text',
        sort: { icon: 'standard', key: 'titolo' }, width: '230px' },
      { column: 'fonte', header: 'Fonte', template: 'text',
        sort: { icon: 'standard', key: 'fonte' }, width: '120px' },
      { column: 'dataOperazione', header: 'Data ins.', template: 'date',
        sort: { icon: 'standard', key: 'dataOperazione' }, width: '150px' },
      { column: 'dataUltAgg', header: 'Ult. modifica', template: 'date',
        sort: { icon: 'standard', key: 'dataUltAgg' }, width: '100px' },
      { column: 'dominio', header: 'Dominio', template: 'text',
        sort: { icon: 'standard', key: 'infoPatrimonio$dominio$descrizione' }, width: '150px' },
      { column: 'statoCompilazione', header: 'Stato compilazione', template: 'chips',
        sort: { icon: 'standard', key: 'statoVisualizzazione' },
        width: '100px',
        chips: {
          key: 'statoCompilazione',
          rules: {
            'FINALIZZATA': { 'background-color': '#D4EDDA', 'color': '#155724' },
            'COMPLETATA': { 'background-color': '#D4EDDA', 'color': '#155724' },
            'IN CORSO': { 'background-color': '#FFF3CD', 'color': '#856404' },
          }
        } },
      { column: 'statoVisualizzazione', header: 'Stato visualizzazione', template: 'text',
        sort: { icon: 'standard', key: 'statoVisualizzazione' },
      },
      { column: 'actions', header: 'Azioni', template: 'action', width: '80px', stickyEnd: true,
        actionListButton: [
          { name: 'Elimina', icon: 'assets/table/trash.svg', action: 'delete' },
        ] },

    ],
  });

  ngOnInit(): void {
    this.fetch();

    this.childActive.set(this.#router.url.includes('/mp/my-area/step-wizard'));
    this.#router.events.pipe(filter((e: any) => e instanceof NavigationEnd)).subscribe(() => {
      const wasChildActive = this.childActive();
      const isNowChildActive = this.#router.url.includes('/mp/my-area/step-wizard');

      this.childActive.set(isNowChildActive);

      if (wasChildActive && !isNowChildActive) {
        this.fetch();
      }
    });
  }

  async openNuovaCollezione(): Promise<void> {
    if (this.useModal) {
      const { result } = await this.#modalService.open(NuovaCollezioneModalComponent as any);
      const data = await result;
      if (data) {
        this.#wizardService.useModal.set(true);
        this.#wizardService.setResponse(data);
        this.#router.navigate(['/mp/my-area/step-wizard']);
      }
    } else {
      this.#wizardService.useModal.set(false);
      this.#wizardService.setResponse(null);
      this.#router.navigate(['/mp/my-area/step-wizard']);
    }
  }

  onSearch(term: string | null): void {
    this.searchTerm.set(term);
    this.state.update((s) => ({
      ...s,
      paginazione: { ...s.paginazione, page: 0 },
    }));
    this.fetch();
  }

  goToPage(page: number): void {
    const clamped = Math.min(Math.max(page - 1, 0), Math.max(this.totalPages() - 1, 0));
    this.state.update((s) => ({
      ...s,
      paginazione: { ...s.paginazione, page: clamped },
    }));
    this.fetch();
  }

  private buildParams(): Record<string, string | number | boolean> {
    const s = this.state();
    const params: Record<string, string | number | boolean> = {
      'paginazione.totalPages': s.paginazione.totalPages ?? 0,
      'paginazione.totalElements': s.paginazione.totalElements ?? 0,
      'paginazione.totalElementsForPage': s.paginazione.totalElementsForPage,
      'paginazione.page': s.paginazione.page,
      order: (s.order ?? '').toString().toLowerCase(),
      orderField: s.orderField ?? '',
    };
    const term = this.searchTerm();
    if (term) {
      params['titoloLike'] = term;
    }
    return params;
  }

  fetch(): void {
    this.loading.set(true);
    this.#tableService.getAllCollezioni(this.buildParams()).subscribe({
      next: (res: CollezioniResponse) => {
        const elements = res?.elements ?? [];

        //shorten titolo if too long
        elements.forEach((el: any) => {
          if (el.titolo && el.titolo.length > 25) {
            el.titolo = el.titolo.slice(0, 25) + '...';
          }
        });

        this.row.set(elements.map((el: any) => ({
          ...el,
          copertina: (el.visualizzazioneMappa?.immagini?.find((img: any) => img?.isCopertina) ?? el.visualizzazioneMappa?.immagini?.[0])?.logoImmagine || 'assets/img/temp_img.png',
          fonte: this.formatFonte(el.fonte),
          dataOperazione: el.dataOperazione,
          dataUltAgg: el.dataUltAgg ,
          dominio:el.infoPatrimonio?.dominio ? el.infoPatrimonio?.dominio?.map((d: any) => d.codice).join(', '):  'Non specificato',
          statoCompilazione: (!el.visualizzazioneMappa?.mostraMappa ) || (el.visualizzazioneMappa === null) ? 'IN CORSO' : 'FINALIZZATA',
        statoVisualizzazione: el.visualizzazioneMappa?.mostraMappa ? 'Online' : 'Offline',
        })));

        // paginazione
        const pg = res?.page ?? {};
        const size =
          Number(
            pg.totalElementsForPage ??
              this.state().paginazione.totalElementsForPage
          ) || 10;
        const page = Number(pg.page ?? this.state().paginazione.page) || 0;
        let totalElements = Number(pg.totalElements ?? 0);
        let totalPages = Number(pg.totalPages ?? 0);

        const seen = page * size + elements.length;
        if (!Number.isFinite(totalElements) || totalElements < 0)
          totalElements = 0;
        if (totalElements === 0 && elements.length > 0) totalElements = seen;
        if (!Number.isFinite(totalPages) || totalPages <= 0) {
          totalPages = size > 0 ? Math.ceil(totalElements / size) : 0;
        }

        // sort
        const rawOrder = (res?.order ?? this.state().order ?? '')
          .toString()
          .toLowerCase();
        const order: SortDir =
          rawOrder === 'asc'
            ? 'asc'
            : rawOrder === 'desc'
            ? 'desc'
            : this.state().order;
        const orderField = (res?.orderField ?? this.state().orderField) || null;

        this.state.update((cur) => ({
          ...cur,
          order,
          orderField,
          paginazione: {
            totalPages,
            totalElements,
            totalElementsForPage: size,
            page,
          },
        }));
      },
      error: (err) => console.error('getAllCollezioni error', err),
      complete: () => this.loading.set(false),
    });
  }

  onTableAction(event: { type: 'sort' | 'action'; key: any; row?: any }): void {
    if (event.type === 'sort') {
      const currentField = this.state().orderField;
      const currentOrder = this.state().order;
      const newOrder: SortDir =
        currentField === event.key && currentOrder === 'asc' ? 'desc' : 'asc';
      this.state.update((s) => ({
        ...s,
        orderField: event.key,
        order: newOrder,
        paginazione: { ...s.paginazione, page: 0 },
      }));
      this.fetch();
    } else if (event.type === 'action') {
      if (event.key === 'open' && event.row) {
        this.#wizardService.setResponse(event.row);
        this.#router.navigate(['/mp/my-area/step-wizard']);
      } else if (event.key === 'delete' && event.row) {
        this.onDeleteRow(event.row);
      }
    }
  }

  onRowClick(row: any): void {
    this.#wizardService.setResponse(row);
    this.#router.navigate(['/mp/my-area/step-wizard']);
  }

  private async onDeleteRow(row: any): Promise<void> {
    const id = row?.id;
    if (!id) {
      return;
    }

    const { result } = await this.#modalService.open(ConfirmDialogComponent, {
      title: 'Elimina collezione',
      message: `Vuoi eliminare la collezione "${row?.titolo ?? ''}"? L'operazione non è reversibile.`,
      confirmLabel: 'Elimina',
      cancelLabel: 'Annulla',
      confirmClass: 'error custom-button-lg',
    });

    const confirmed = await result;
    if (!confirmed) {
      return;
    }

    this.#tableService.deleteCollezione(id).subscribe({
      next: () => {
        void this.#snackbar.show('Collezione eliminata con successo.', 'success');
        this.fetch();
      },
      error: (err) => {
        console.error('deleteCollezione error', err);
        void this.#snackbar.show(
          "Errore durante l'eliminazione della collezione.",
          'error',
        );
      },
    });
  }

  private formatFonte(value: unknown): string {
    if (typeof value !== 'string' || !value.trim()) {
      return 'Non specificata';
    }

    const source = value.trim();
    if (source.toLowerCase() === 'mp') {
      return 'Mappa';
    }
    if (source.toLowerCase() === 'ipac') {
      return 'IPaC';
    }

    return source;
  }


}
